import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
  TextField
} from '@mui/material';
import { PokemonV2 } from '../../pokemon/pokemon';
import { usePokemonList } from '../../pokemon/usePokemonList';
import { calculateOffenceSpeed } from '../../lib/stat';
import { PersonalityRate } from '../../types/damage';
import { OFFENCE_ITEM_ID_KODAWARISUKAFU } from '../../types/item';

const TOP_USAGE_LIMIT_30 = 30;
const TOP_USAGE_LIMIT_150 = 150;

interface SpeedComparison {
  pokemon: PokemonV2;
  speed: number;
  difference: number;
  status: 'faster' | 'same' | 'slower';
  isMaxSpec?: boolean;
  isCurrentPokemon?: boolean;
  isMaxSpeedVersion?: boolean;
  isMinSpeedVersion?: boolean;
  isMidSpeedVersion?: boolean;
  isNoEffortVersion?: boolean;
  isMaxSpeedScarfVersion?: boolean;
  isMidSpeedScarfVersion?: boolean;
  speedType?: 'max' | 'mid' | 'no' | 'min' | 'max_scarf' | 'mid_scarf';
}

interface SpeedModePageProps {
  selectedPokemon: PokemonV2 | null;
}

function SpeedModePage({ selectedPokemon }: SpeedModePageProps) {
  const { pokemonList } = usePokemonList();
  const [displayMode, setDisplayMode] = useState<'top30' | 'top150' | 'all'>('top30');
  const [showMaxSpeedVersion, setShowMaxSpeedVersion] = useState(true);
  const [showMinSpeedVersion, setShowMinSpeedVersion] = useState(false);
  const [showMidSpeedVersion, setShowMidSpeedVersion] = useState(false);
  const [showNoEffortVersion, setShowNoEffortVersion] = useState(false);
  const [showMaxSpeedScarfVersion, setShowMaxSpeedScarfVersion] = useState(false);
  const [showMidSpeedScarfVersion, setShowMidSpeedScarfVersion] = useState(false);
  const [speedBaseStatMin, setSpeedBaseStatMin] = useState<number>(0);
  const [speedBaseStatMax, setSpeedBaseStatMax] = useState<number>(255);
  
  // 現在のポケモンの素早さを計算（左側ポケモンの設定値を使用）
  const currentSpeed = useMemo(() => {
    if (!selectedPokemon) return 0;
    
    // 基本素早さ実数値を計算
    let baseSpeed = calculateOffenceSpeed(selectedPokemon);
    
    // ランク補正を適用
    const rankMultiplier = selectedPokemon.rankCorrection?.speed || 0;
    if (rankMultiplier !== 0) {
      const multipliers = [0.25, 0.28, 0.33, 0.4, 0.5, 0.66, 1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0];
      const index = Math.max(0, Math.min(12, rankMultiplier + 6));
      baseSpeed = Math.floor(baseSpeed * multipliers[index]);
    }
    
    // こだわりスカーフの効果を適用（1.5倍、切り捨て）
    if (selectedPokemon.selected_item_id === OFFENCE_ITEM_ID_KODAWARISUKAFU) { // こだわりスカーフのID
      baseSpeed = Math.floor(baseSpeed * 1.5);
    }
    
    return baseSpeed;
  }, [selectedPokemon]);
  
  // 他のポケモンとの比較データを生成
  const comparisonData = useMemo(() => {
    if (!selectedPokemon) return [];
    
    const comparisons: SpeedComparison[] = [];
    
    // 表示モードに応じてポケモンを選択し、素早さ種族値で絞り込み
    let targetPokemons = 
      displayMode === 'top30' ? pokemonList.slice(0, TOP_USAGE_LIMIT_30) :
      displayMode === 'top150' ? pokemonList.slice(0, TOP_USAGE_LIMIT_150) :
      pokemonList;
    
    // 素早さ種族値による絞り込み
    targetPokemons = targetPokemons.filter(pokemon => 
      pokemon.speed >= speedBaseStatMin && pokemon.speed <= speedBaseStatMax
    );
    
    // 左側ポケモンを現在の設定で追加
    if (selectedPokemon) {
      const difference = currentSpeed - currentSpeed; // 0
      const status: 'faster' | 'same' | 'slower' = 'same';
      
      // 左側ポケモンが最速仕様かチェック
      const isMaxSpec = selectedPokemon.personality?.speed === 1.1 && 
                       selectedPokemon.effective_slider_step?.speed === 32;
      
      comparisons.push({
        pokemon: selectedPokemon,
        speed: currentSpeed,
        difference: Math.abs(difference),
        status,
        isCurrentPokemon: true,
        isMaxSpec
      });
    }
    
    targetPokemons.forEach(pokemon => {
      // 最速設定の処理
      if (showMaxSpeedVersion) {
        // 最速設定：性格補正+1.1倍、努力値32、個体値31
        const maxSpeedPokemon = {
          ...pokemon,
          effective_slider_step: {
            ...pokemon.effective_slider_step,
            speed: 32 // 努力値32
          },
          personality: {
            ...pokemon.personality,
            speed: 1.1 as PersonalityRate // 素早さ+補正（おくびょう/ようき）
          },
          individualValue: {
            ...pokemon.individualValue,
            speed: 31 as any // 個体値31
          }
        };
        
        const maxPokemonSpeed = calculateOffenceSpeed(maxSpeedPokemon as any);
        
        const maxDifference = maxPokemonSpeed - currentSpeed;
        const maxStatus: 'faster' | 'same' | 'slower' = 
          maxDifference > 0 ? 'faster' : maxDifference < 0 ? 'slower' : 'same';
        
        // 左側ポケモンと同じIDの場合、最速版として追加
        const isMaxSpeedVersion = selectedPokemon && pokemon.id === selectedPokemon.id;
        
        comparisons.push({
          pokemon,
          speed: maxPokemonSpeed,
          difference: Math.abs(maxDifference),
          status: maxStatus,
          isMaxSpeedVersion,
          isMaxSpec: true, // 比較用ポケモンは全て最速仕様
          speedType: 'max'
        });
      }
      
      // 準速設定の処理
      if (showMidSpeedVersion) {
        // 準速設定：性格補正なし、努力値32、個体値31
        const midSpeedPokemon = {
          ...pokemon,
          effective_slider_step: {
            ...pokemon.effective_slider_step,
            speed: 32 // 努力値32
          },
          personality: {
            ...pokemon.personality,
            speed: 1.0 as PersonalityRate // 素早さ補正なし
          },
          individualValue: {
            ...pokemon.individualValue,
            speed: 31 as any // 個体値31
          }
        };
        
        const midPokemonSpeed = calculateOffenceSpeed(midSpeedPokemon as any);
        
        const midDifference = midPokemonSpeed - currentSpeed;
        const midStatus: 'faster' | 'same' | 'slower' = 
          midDifference > 0 ? 'faster' : midDifference < 0 ? 'slower' : 'same';
        
        // 左側ポケモンと同じIDの場合、準速版として追加
        const isMidSpeedVersion = selectedPokemon && pokemon.id === selectedPokemon.id;
        
        comparisons.push({
          pokemon,
          speed: midPokemonSpeed,
          difference: Math.abs(midDifference),
          status: midStatus,
          isMidSpeedVersion,
          speedType: 'mid'
        });
      }
      
      // 無振り設定の処理
      if (showNoEffortVersion) {
        // 無振り設定：性格補正なし、努力値0、個体値31
        const noEffortPokemon = {
          ...pokemon,
          effective_slider_step: {
            ...pokemon.effective_slider_step,
            speed: 0 // 努力値0
          },
          personality: {
            ...pokemon.personality,
            speed: 1.0 as PersonalityRate // 素早さ補正なし
          },
          individualValue: {
            ...pokemon.individualValue,
            speed: 31 as any // 個体値31
          }
        };
        
        const noEffortPokemonSpeed = calculateOffenceSpeed(noEffortPokemon as any);
        
        const noEffortDifference = noEffortPokemonSpeed - currentSpeed;
        const noEffortStatus: 'faster' | 'same' | 'slower' = 
          noEffortDifference > 0 ? 'faster' : noEffortDifference < 0 ? 'slower' : 'same';
        
        // 左側ポケモンと同じIDの場合、無振り版として追加
        const isNoEffortVersion = selectedPokemon && pokemon.id === selectedPokemon.id;
        
        comparisons.push({
          pokemon,
          speed: noEffortPokemonSpeed,
          difference: Math.abs(noEffortDifference),
          status: noEffortStatus,
          isNoEffortVersion,
          speedType: 'no'
        });
      }
      
      // 最遅設定の処理
      if (showMinSpeedVersion) {
        // 最遅設定：性格補正-0.9倍、努力値0、個体値0
        const minSpeedPokemon = {
          ...pokemon,
          effective_slider_step: {
            ...pokemon.effective_slider_step,
            speed: 0 // 努力値0
          },
          personality: {
            ...pokemon.personality,
            speed: 0.9 as PersonalityRate // 素早さ-補正（のんき/なまいき等）
          },
          individualValue: {
            ...pokemon.individualValue,
            speed: 0 as any // 個体値0
          }
        };
        
        const minPokemonSpeed = calculateOffenceSpeed(minSpeedPokemon as any);
        
        const minDifference = minPokemonSpeed - currentSpeed;
        const minStatus: 'faster' | 'same' | 'slower' = 
          minDifference > 0 ? 'faster' : minDifference < 0 ? 'slower' : 'same';
        
        // 左側ポケモンと同じIDの場合、最遅版として追加
        const isMinSpeedVersion = selectedPokemon && pokemon.id === selectedPokemon.id;
        
        comparisons.push({
          pokemon,
          speed: minPokemonSpeed,
          difference: Math.abs(minDifference),
          status: minStatus,
          isMinSpeedVersion,
          speedType: 'min'
        });
      }
      
      // 最速スカーフ設定の処理
      if (showMaxSpeedScarfVersion) {
        // 最速スカーフ設定：性格補正+1.1倍、努力値32、個体値31、こだわりスカーフ
        const maxScarfSpeedPokemon = {
          ...pokemon,
          effective_slider_step: {
            ...pokemon.effective_slider_step,
            speed: 32 // 努力値32
          },
          personality: {
            ...pokemon.personality,
            speed: 1.1 as PersonalityRate // 素早さ+補正（おくびょう/ようき）
          },
          individualValue: {
            ...pokemon.individualValue,
            speed: 31 as any // 個体値31
          }
        };
        
        let maxScarfPokemonSpeed = calculateOffenceSpeed(maxScarfSpeedPokemon as any);
        // こだわりスカーフの効果を適用（1.5倍、切り捨て）
        maxScarfPokemonSpeed = Math.floor(maxScarfPokemonSpeed * 1.5);
        
        const maxScarfDifference = maxScarfPokemonSpeed - currentSpeed;
        const maxScarfStatus: 'faster' | 'same' | 'slower' = 
          maxScarfDifference > 0 ? 'faster' : maxScarfDifference < 0 ? 'slower' : 'same';
        
        // 左側ポケモンと同じIDの場合、最速スカーフ版として追加
        const isMaxSpeedScarfVersion = selectedPokemon && pokemon.id === selectedPokemon.id;
        
        comparisons.push({
          pokemon,
          speed: maxScarfPokemonSpeed,
          difference: Math.abs(maxScarfDifference),
          status: maxScarfStatus,
          isMaxSpeedScarfVersion,
          speedType: 'max_scarf'
        });
      }
      
      // 準速スカーフ設定の処理
      if (showMidSpeedScarfVersion) {
        // 準速スカーフ設定：性格補正なし、努力値32、個体値31、こだわりスカーフ
        const midScarfSpeedPokemon = {
          ...pokemon,
          effective_slider_step: {
            ...pokemon.effective_slider_step,
            speed: 32 // 努力値32
          },
          personality: {
            ...pokemon.personality,
            speed: 1.0 as PersonalityRate // 素早さ補正なし
          },
          individualValue: {
            ...pokemon.individualValue,
            speed: 31 as any // 個体値31
          }
        };
        
        let midScarfPokemonSpeed = calculateOffenceSpeed(midScarfSpeedPokemon as any);
        // こだわりスカーフの効果を適用（1.5倍、切り捨て）
        midScarfPokemonSpeed = Math.floor(midScarfPokemonSpeed * 1.5);
        
        const midScarfDifference = midScarfPokemonSpeed - currentSpeed;
        const midScarfStatus: 'faster' | 'same' | 'slower' = 
          midScarfDifference > 0 ? 'faster' : midScarfDifference < 0 ? 'slower' : 'same';
        
        // 左側ポケモンと同じIDの場合、準速スカーフ版として追加
        const isMidSpeedScarfVersion = selectedPokemon && pokemon.id === selectedPokemon.id;
        
        comparisons.push({
          pokemon,
          speed: midScarfPokemonSpeed,
          difference: Math.abs(midScarfDifference),
          status: midScarfStatus,
          isMidSpeedScarfVersion,
          speedType: 'mid_scarf'
        });
      }
    });
    
    // 素早さの降順でソート
    const sortedComparisons = comparisons.sort((a, b) => {
      return b.speed - a.speed;
    });
    
    return sortedComparisons;
  }, [
    selectedPokemon,
    currentSpeed, 
    displayMode,
    pokemonList,
    showMaxSpeedVersion,
    showMidSpeedVersion,
    showNoEffortVersion,
    showMinSpeedVersion,
    showMaxSpeedScarfVersion,
    showMidSpeedScarfVersion,
    speedBaseStatMin,
    speedBaseStatMax
  ]);
  

  if (!selectedPokemon) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          左側でポケモンを選択してください
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
      
      {/* 表示モード切り替えと設定 */}
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={displayMode === 'top30' ? "contained" : "outlined"}
            onClick={() => setDisplayMode('top30')}
            size="small"
          >
            上位30匹
          </Button>
          <Button
            variant={displayMode === 'top150' ? "contained" : "outlined"}
            onClick={() => setDisplayMode('top150')}
            size="small"
          >
            上位150匹
          </Button>
          <Button
            variant={displayMode === 'all' ? "contained" : "outlined"}
            onClick={() => setDisplayMode('all')}
            size="small"
          >
            全表示
          </Button>
        </Box>
        
        {/* 最速・準速・無振り・最遅ポケモン表示チェックボックスと種族値絞り込み */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showMaxSpeedScarfVersion}
                onChange={(e) => setShowMaxSpeedScarfVersion(e.target.checked)}
                size="small"
              />
            }
            label="最ス"
            sx={{ fontSize: '0.875rem' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showMaxSpeedVersion}
                onChange={(e) => setShowMaxSpeedVersion(e.target.checked)}
                size="small"
              />
            }
            label="最速"
            sx={{ fontSize: '0.875rem' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showMidSpeedScarfVersion}
                onChange={(e) => setShowMidSpeedScarfVersion(e.target.checked)}
                size="small"
              />
            }
            label="準ス"
            sx={{ fontSize: '0.875rem' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showMidSpeedVersion}
                onChange={(e) => setShowMidSpeedVersion(e.target.checked)}
                size="small"
              />
            }
            label="準"
            sx={{ fontSize: '0.875rem' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showNoEffortVersion}
                onChange={(e) => setShowNoEffortVersion(e.target.checked)}
                size="small"
              />
            }
            label="無"
            sx={{ fontSize: '0.875rem' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showMinSpeedVersion}
                onChange={(e) => setShowMinSpeedVersion(e.target.checked)}
                size="small"
              />
            }
            label="最遅"
            sx={{ fontSize: '0.875rem' }}
          />
          
          {/* 素早さ種族値による絞り込み */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
              種族値:
            </Typography>
            <TextField
              label="最小"
              type="number"
              value={speedBaseStatMin}
              onChange={(e) => setSpeedBaseStatMin(Math.max(0, Math.min(255, parseInt(e.target.value) || 0)))}
              size="small"
              sx={{ 
                width: 80,
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                  '-webkit-appearance': 'none',
                  margin: 0,
                },
                '& input[type=number]': {
                  '-moz-appearance': 'textfield',
                },
              }}
              inputProps={{ min: 0, max: 255 }}
            />
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              〜
            </Typography>
            <TextField
              label="最大"
              type="number"
              value={speedBaseStatMax}
              onChange={(e) => setSpeedBaseStatMax(Math.max(0, Math.min(255, parseInt(e.target.value) || 255)))}
              size="small"
              sx={{ 
                width: 80,
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                  '-webkit-appearance': 'none',
                  margin: 0,
                },
                '& input[type=number]': {
                  '-moz-appearance': 'textfield',
                },
              }}
              inputProps={{ min: 0, max: 255 }}
            />
          </Box>
        </Box>
      </Box>
      
      {/* 素早さ比較マトリックス */}
      <Typography variant="h6" gutterBottom>
        素早さ比較
      </Typography>
      
      <Grid container spacing={1}>
        {comparisonData.map((comparison) => {
          // 背景色を決定
          const backgroundColor = 
            comparison.status === 'faster' ? '#ffcdd2' :  // 薄赤（抜かれる）
            comparison.status === 'same' ? '#fff9c4' :    // 黄色（同速）
            '#c8e6c9';                                    // 緑色（抜ける）
          
          // 現在のポケモン（左側）かどうかを判定
          const isSelected = comparison.isCurrentPokemon;
          
          return (
            <Grid size={{ xs: 2, sm: 1.5, md: 1.2 }} key={`${comparison.pokemon.id}-${comparison.isCurrentPokemon ? 'current' : comparison.speedType || 'normal'}`}>
              <Card
                sx={{
                  backgroundColor,
                  height: 90,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  border: isSelected ? '3px solid #d32f2f' : 'none',
                  '&:hover': {
                    boxShadow: 2,
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <CardContent sx={{ p: 0.5, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5, '&:last-child': { pb: 0.5 } }}>
                  {/* ポケモンアイコン */}
                  {comparison.pokemon.picture_url && (
                    <Box
                      component="img"
                      src={comparison.pokemon.picture_url}
                      alt={comparison.pokemon.name_ja}
                      sx={{
                        width: 64,
                        height: 64,
                        imageRendering: 'pixelated',
                        transform: 'scaleX(1)',
                        flexShrink: 0
                      }}
                    />
                  )}
                  
                  {/* 数値表示エリア */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                    {/* 素早さ実数値 */}
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1, fontWeight: 'bold' }}>
                      {comparison.speed}
                    </Typography>
                    
                    {/* 差分表示 */}
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: 1, fontWeight: 'bold', color: comparison.status === 'faster' ? '#d32f2f' : comparison.status === 'slower' ? '#388e3c' : '#f57c00' }}>
                      {comparison.status === 'faster' ? `+${comparison.difference}` :
                       comparison.status === 'slower' ? `-${comparison.difference}` :
                       '±0'}
                    </Typography>
                  </Box>
                  
                  {/* 仕様マーカー */}
                  {!comparison.isCurrentPokemon && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: isSelected ? 5 : 2,
                        right: isSelected ? 5 : 2,
                        backgroundColor: 'white',
                        color: 'black',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        padding: '1px 3px',
                        borderRadius: '2px',
                        border: '1px solid #000',
                        zIndex: 1
                      }}
                    >
                      {comparison.speedType === 'max_scarf' ? '最ス' : 
                       comparison.speedType === 'max' ? '最' : 
                       comparison.speedType === 'mid_scarf' ? '準ス' :
                       comparison.speedType === 'mid' ? '準' :
                       comparison.speedType === 'no' ? '無' :
                       comparison.speedType === 'min' ? '最遅' : ''}
                    </Box>
                  )}
                  
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export default SpeedModePage;
